import { useMemo, type ReactNode } from 'react';
import {
    type ListFinderVehiclesQueryVariables,
    useListFinderVehiclesQuery,
} from '../../api/queries/listFinderVehicles';
import TransferField, { type TransferFieldProps } from '../../components/fields/TransferField';
import CollapsibleWrapper, { Panel } from '../../components/wrappers/CollapsibleWrapper';
import useTranslatedString from '../../utilities/useTranslatedString';
import SyncFinderVehicleButton from '../admin/FinderVehicleListPage/SyncFinderVehicleButton';

type RecordType = {
    key: string;
    title: string;
};

type FinderVehicleAssignmentsSectionProps = {
    header: ReactNode;
    name: string;
    filter?: ListFinderVehiclesQueryVariables['filter'];
    transferFieldProps?: Omit<TransferFieldProps<RecordType>, 'name' | 'dataSource'>;
    allowSync: boolean;
};

const filterOption = (inputValue: string, option: RecordType) =>
    option.title.toLowerCase().indexOf(inputValue.toLowerCase()) > -1;

const FinderVehicleAssignmentsSection = ({
    header,
    name,
    filter,
    transferFieldProps,
    allowSync,
}: FinderVehicleAssignmentsSectionProps) => {
    const translatedString = useTranslatedString();

    const { data: finderVehicleData } = useListFinderVehiclesQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter },
    });

    const vehicleDataSource: RecordType[] = useMemo(() => {
        if (!finderVehicleData) {
            return [];
        }

        return finderVehicleData.vehicles.items.map(vehicle => ({
            key: vehicle.versioning.suiteId,
            title: `${translatedString(vehicle.name)} (${vehicle.listing.id})`,
        }));
    }, [finderVehicleData, translatedString]);

    return (
        <CollapsibleWrapper defaultActiveKey={name}>
            <Panel key={name} header={header}>
                <TransferField
                    dataSource={vehicleDataSource}
                    filterOption={filterOption}
                    listStyle={{ width: '100%' }}
                    name={name}
                    render={item => item.title}
                    {...transferFieldProps}
                />
                {filter?.companyId && allowSync && <SyncFinderVehicleButton companyId={filter?.companyId} />}
            </Panel>
        </CollapsibleWrapper>
    );
};

export default FinderVehicleAssignmentsSection;
